import React, { Suspense, lazy, ComponentType } from 'react';
import { Skeleton } from '../components/ui/SkeletonLoader';

/**
 * Lazy loading utilities for code splitting and performance optimization
 */

// Enhanced loading fallback component
const LoadingFallback: React.FC<{ 
  type?: 'page' | 'component' | 'chart' | 'table';
  className?: string;
}> = ({ type = 'page', className }) => {
  switch (type) {
    case 'page':
      return (
        <div className={`min-h-screen bg-gray-50 ${className || ''}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Header skeleton */}
            <div className="mb-8">
              <Skeleton className="h-8 w-64 mb-4" />
              <Skeleton className="h-4 w-96" />
            </div>
            
            {/* Content skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-white rounded-lg border p-6">
                  <Skeleton className="h-6 w-48 mb-4" />
                  <div className="space-y-3">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-5/6" />
                  </div>
                </div>
                <div className="bg-white rounded-lg border p-6">
                  <Skeleton className="h-6 w-32 mb-4" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Skeleton className="h-32" />
                    <Skeleton className="h-32" />
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <div className="bg-white rounded-lg border p-6">
                  <Skeleton className="h-6 w-40 mb-4" />
                  <div className="space-y-2">
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-4/5" />
                    <Skeleton className="h-3 w-3/5" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    
    case 'table':
      return (
        <div className={`bg-white border border-gray-200 rounded-lg overflow-hidden ${className || ''}`}>
          <div className="border-b border-gray-200 p-4">
            <div className="grid gap-4 grid-cols-1 md:grid-cols-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} className="h-4 w-24" />
              ))}
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="p-4">
                <div className="grid gap-4 grid-cols-1 md:grid-cols-4">
                  {Array.from({ length: 4 }).map((_, colIndex) => (
                    <Skeleton key={colIndex} className="h-4" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    
    case 'chart':
      return (
        <div className={`bg-white border border-gray-200 rounded-lg p-6 ${className || ''}`}>
          <Skeleton className="h-6 w-48 mb-4" />
          <Skeleton className="h-64 w-full" />
        </div>
      );
    
    case 'component':
    default:
      return (
        <div className={`bg-white border border-gray-200 rounded-lg p-6 ${className || ''}`}>
          <Skeleton className="h-6 w-32 mb-4" />
          <div className="space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-5/6" />
          </div>
        </div>
      );
  }
};

// Higher-order component for lazy loading with custom fallback
export const withLazyLoading = <P extends object = {}>(
  importFn: () => Promise<{ default: ComponentType<P> }>,
  fallbackType: 'page' | 'component' | 'chart' | 'table' = 'component',
  fallbackClassName?: string
) => {
  const LazyComponent = lazy(importFn);

  const WrappedComponent: React.FC<P> = (props) => (
    <Suspense fallback={<LoadingFallback type={fallbackType} className={fallbackClassName} />}>
      <LazyComponent {...props} />
    </Suspense>
  );

  WrappedComponent.displayName = `LazyLoaded(${LazyComponent.displayName || 'Component'})`;

  return WrappedComponent;
};

// Helper for named exports
export const withNamedExportLazyLoading = <P extends object = {}>(
  importFn: () => Promise<any>,
  exportName: string,
  fallbackType: 'page' | 'component' | 'chart' | 'table' = 'component',
  fallbackClassName?: string
) => {
  const LazyComponent = lazy(() =>
    importFn().then(module => ({ default: module[exportName] }))
  );

  const WrappedComponent: React.FC<P> = (props) => (
    <Suspense fallback={<LoadingFallback type={fallbackType} className={fallbackClassName} />}>
      <LazyComponent {...props} />
    </Suspense>
  );

  WrappedComponent.displayName = `LazyLoaded(${exportName})`;

  return WrappedComponent;
};

// Preload function for critical resources
export const preloadComponent = (importFn: () => Promise<any>) => {
  // Start loading the component but don't wait for it
  importFn().catch(() => {
    // Silently handle preload failures
  });
};

// Enhanced preloading with priority and timing
export const preloadComponentWithPriority = (
  importFn: () => Promise<any>,
  priority: 'high' | 'medium' | 'low' = 'medium',
  delay = 0
) => {
  const actualDelay = priority === 'high' ? delay : priority === 'medium' ? delay + 500 : delay + 2000;

  setTimeout(() => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        preloadComponent(importFn);
      }, { timeout: priority === 'high' ? 1000 : 5000 });
    } else {
      preloadComponent(importFn);
    }
  }, actualDelay);
};

// Preload multiple components in sequence
export const preloadComponentSequence = (
  components: Array<{
    importFn: () => Promise<any>;
    priority?: 'high' | 'medium' | 'low';
    delay?: number;
  }>
) => {
  components.forEach(({ importFn, priority = 'medium', delay = 0 }) => {
    preloadComponentWithPriority(importFn, priority, delay);
  });
};

// Lazy load with retry mechanism
export const withRetryLazyLoading = <P extends object>(
  importFn: () => Promise<{ default: ComponentType<P> }>,
  maxRetries = 3,
  fallbackType: 'page' | 'component' | 'chart' | 'table' = 'component'
) => {
  const LazyComponent = lazy(() => {
    let retries = 0;
    
    const loadWithRetry = async (): Promise<{ default: ComponentType<P> }> => {
      try {
        return await importFn();
      } catch (error) {
        if (retries < maxRetries) {
          retries++;
          // Exponential backoff
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, retries) * 1000));
          return loadWithRetry();
        }
        throw error;
      }
    };
    
    return loadWithRetry();
  });
  
  const WrappedComponent: React.FC<P> = (props) => (
    <Suspense fallback={<LoadingFallback type={fallbackType} />}>
      <LazyComponent {...props} />
    </Suspense>
  );
  
  WrappedComponent.displayName = `RetryLazyLoaded(${LazyComponent.displayName || 'Component'})`;
  
  return WrappedComponent;
};

// Intersection observer based lazy loading for components
export const withIntersectionLazyLoading = <P extends object>(
  importFn: () => Promise<{ default: ComponentType<P> }>,
  options: IntersectionObserverInit = { threshold: 0.1 }
) => {
  const LazyComponent = lazy(importFn);
  
  const WrappedComponent: React.FC<P> = (props) => {
    const [shouldLoad, setShouldLoad] = React.useState(false);
    const ref = React.useRef<HTMLDivElement>(null);
    
    React.useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            observer.disconnect();
          }
        },
        options
      );
      
      if (ref.current) {
        observer.observe(ref.current);
      }
      
      return () => observer.disconnect();
    }, []);
    
    return (
      <div ref={ref}>
        {shouldLoad ? (
          <Suspense fallback={<LoadingFallback type="component" />}>
            <LazyComponent {...props} />
          </Suspense>
        ) : (
          <LoadingFallback type="component" />
        )}
      </div>
    );
  };
  
  WrappedComponent.displayName = `IntersectionLazyLoaded(${LazyComponent.displayName || 'Component'})`;
  
  return WrappedComponent;
};

// Bundle size analyzer helper (development only)
export const logBundleInfo = (componentName: string) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Bundle] Loading ${componentName} chunk`);
  }
};

export { LoadingFallback };
